// =====================================================
// R-SIS-1 — Jalali (Shamsi) ⇄ Gregorian conversion + calendar math.
//
// The SIS stores all dates as Gregorian ISO (like every other model);
// the UI lets users read/enter them in Jalali. `i18n/format.js` already
// formats Gregorian→Jalali for *display*; this module adds the inverse
// (so a JalaliDatePicker can turn a user's picked Shamsi date into an ISO
// string) plus the calendar arithmetic a month-grid needs (leap years +
// month lengths). Standard fallback algorithm — no ICU dependency.
// =====================================================

import { getLocale, type AppLocale } from "./locale-state";
import { localeMetadata } from "./locale-registry";
import { formatLocaleDate, intlLocale, localeNumberFormatter, normaliseDecimalDigits } from "./intl";
import { localeDateFormatter } from "./intl-fallback";

export interface JalaliParts {
  jy: number;
  jm: number; // 1..12
  jd: number; // 1..31
}

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
/** Arabic-Indic digits, U+0660..U+0669 (STYLE_AR §٣). Visually close to the
 *  Persian set above but a DIFFERENT code point for ٤/٥/٦ — mixing them is a
 *  silent bug, which is why neither set is ever copied from the other. */
const AR_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
const LATIN_DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const digitSets = new Map<AppLocale, readonly string[]>();

/** The digit set declared by the 50-locale registry. */
const digitsFor = (locale: AppLocale): readonly string[] => {
  if (locale === "fa") return FA_DIGITS;
  if (locale === "ar") return AR_DIGITS;
  const cached = digitSets.get(locale);
  if (cached) return cached;
  try {
    const formatter = localeNumberFormatter(intlLocale(locale), { useGrouping: false });
    const digits = LATIN_DIGITS.map((_, index) => formatter.format(index));
    digitSets.set(locale, digits);
    return digits;
  } catch {
    return LATIN_DIGITS;
  }
};

/**
 * Latin digits → the reader's digits.
 *
 * S-I18N-W1a: kept under its historical name for its ~830 call sites, but it
 * is locale-aware now — every platform locale follows its registry numbering
 * system (`arabext`, `arab`, `deva`, `latn`, …).
 * Callers that must pin one script use `toFaDigitsExplicit` / `toArDigits`.
 */
export const toFaDigits = (input: string | number): string => toLocaleDigits(input, getLocale());

/** Locale-explicit form, for tests and for text that is pinned to one language. */
export const toLocaleDigits = (input: string | number, locale: AppLocale): string => {
  const set = digitsFor(locale);
  return String(input ?? "").replace(/[0-9]/g, (d) => set[Number(d)] as string);
};

/** Always Persian digits, regardless of the active locale. */
export const toFaDigitsExplicit = (input: string | number): string => toLocaleDigits(input, "fa");
/** Always Arabic-Indic digits, regardless of the active locale. */
export const toArDigits = (input: string | number): string => toLocaleDigits(input, "ar");

/**
 * Any digit script → Latin, for validation and for what we send to the API.
 *
 * Numeric-input boundary only: accepts every digit family in the registry,
 * including Devanagari keyboards. Letters, separators and canonical stored
 * identifiers are not cleaned, reformatted or case-folded here.
 */
export const toEnDigits = (input: string): string => normaliseDecimalDigits(String(input ?? ""));

export const JALALI_MONTHS_FA = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
] as const;

// Persian week starts Saturday. getDay(): 0=Sun..6=Sat → Saturday index 0.
export const JALALI_WEEKDAYS_FA = ["ش", "ی", "د", "س", "چ", "پ", "ج"] as const;

/**
 * Gregorian month names for `ar` — the INTERNATIONAL forms, hard-coded.
 *
 * STYLE_AR §٤ locks these: the audience spans Riyadh, Cairo, Baghdad and
 * Rabat, so the Levantine set (كانون الثاني, شباط…) is wrong here. They are
 * not read from `Intl` for the same reason this module never reads the Jalali
 * calendar from ICU: the answer differs by ICU version and by region subtag
 * (`ar-SY` yields the Levantine set), and a date that silently changes name
 * between two servers is exactly the class of bug this file exists to prevent.
 */
export const GREGORIAN_MONTHS_AR = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
] as const;

/**
 * Arabic weekday short forms, Saturday-first (same column order as the Persian
 * grid, since the Arabic week also starts on Saturday). Three letters, not one:
 * Arabic has no single-letter weekday convention, and «أ» would stand for both
 * الأحد and الأربعاء.
 */
export const GREGORIAN_WEEKDAYS_AR = ["سبت", "أحد", "إثن", "ثلا", "أرب", "خمي", "جمع"] as const;

/**
 * Jalali leap-year test, derived from the SAME G↔J conversion the rest of this
 * module uses: a year is leap iff the Gregorian span from its Nowruz to the
 * next Nowruz is 366 days. This guarantees leap status, Esfand length and date
 * round-trips can never disagree. (The previous Birashk 33-year arithmetic
 * disagreed with the conversion for years such as 1404/1408 — it wrongly
 * reported them leap, which would let a picker offer the non-existent «۳۰ اسفند».)
 */
export const jalaliIsLeap = (jy: number): boolean => {
  const a = jalaliToGregorian(jy, 1, 1);
  const b = jalaliToGregorian(jy + 1, 1, 1);
  const days = Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86_400_000);
  return days === 366;
};

/** Days in a given Jalali month (1..12). Esfand = 29, or 30 in a leap year. */
export const jalaliMonthLength = (jy: number, jm: number): number => {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return jalaliIsLeap(jy) ? 30 : 29;
};

/** Gregorian → Jalali. gm/gd are 1-based. */
export const gregorianToJalali = (gy: number, gm: number, gd: number): JalaliParts => {
  const gDaysInMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy = gy <= 1600 ? 0 : 979;
  let gy2 = gy <= 1600 ? gy - 621 : gy - 1600;
  const gyForLeap = gm > 2 ? gy2 + 1 : gy2;
  let days =
    365 * gy2 +
    Math.floor((gyForLeap + 3) / 4) -
    Math.floor((gyForLeap + 99) / 100) +
    Math.floor((gyForLeap + 399) / 400) -
    80 +
    gd +
    gDaysInMonth[gm - 1];
  jy += 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
};

/** Jalali → Gregorian. Returns 1-based [gy, gm, gd]. */
export const jalaliToGregorian = (jy: number, jm: number, jd: number): [number, number, number] => {
  let gy = jy <= 979 ? 621 : 1600;
  const jy2 = jy <= 979 ? jy : jy - 979;
  let days =
    365 * jy2 +
    Math.floor(jy2 / 33) * 8 +
    Math.floor(((jy2 % 33) + 3) / 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  gy += 400 * Math.floor(days / 146097);
  days %= 146097;
  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }
  gy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  let gd = days + 1;
  const isGregLeap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
  const gMonthLen = [31, isGregLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let gm = 0;
  while (gm < 12 && gd > gMonthLen[gm]) {
    gd -= gMonthLen[gm];
    gm++;
  }
  return [gy, gm + 1, gd];
};

/** A JS Date (local) → Jalali parts. */
export const dateToJalali = (d: Date): JalaliParts =>
  gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());

/**
 * Jalali parts → a JS Date at local midnight. We build it via the
 * Gregorian Y/M/D so the calendar day is exact regardless of timezone.
 */
export const jalaliToDate = (jy: number, jm: number, jd: number): Date => {
  const [gy, gm, gd] = jalaliToGregorian(jy, jm, jd);
  return new Date(gy, gm - 1, gd);
};

/** ISO datetime string (UTC midnight of the picked day) for API payloads. */
export const jalaliToIso = (jy: number, jm: number, jd: number): string => {
  const [gy, gm, gd] = jalaliToGregorian(jy, jm, jd);
  // Anchor at UTC midnight so the stored instant matches the picked day
  // everywhere (term windows are day-granular).
  return new Date(Date.UTC(gy, gm - 1, gd)).toISOString();
};

/**
 * "۱۴۰۵/۰۶/۳۱" from a Date (or "" if null/invalid).
 *
 * S-UI-JALALI: kept as the historical name for its ~120 existing call sites,
 * but it is now a thin alias of `formatJalali` — so those sites inherit the
 * Tehran anchoring for free instead of silently rendering the viewer's local
 * calendar day. Prefer `formatJalali` in new code.
 */
export const formatJalaliShort = (d: Date | string | null | undefined): string => formatJalali(d);

// =====================================================
// S-UI-JALALI — the ONE canonical Jalali display layer.
//
// Every user-visible date in the product goes through the formatters below.
// Two rules make them safe:
//
//   1. TIME ZONE. A date is a *calendar day in Tehran*, never "whatever day it
//      is on the viewer's machine". A student in Berlin opening a deadline of
//      2026-08-15T21:30:00Z must read the same «۱۴۰۵/۰۵/۲۵» an admin in Tehran
//      reads. We resolve the Gregorian Y/M/D/H/M *in Asia/Tehran* first, then
//      hand those parts to this module's own converter.
//
//   2. NO ICU DEPENDENCE FOR THE CALENDAR. `Intl` is used ONLY to resolve the
//      time zone (with the boring, ICU-version-stable "en-US" numeric parts) —
//      never to render the Persian calendar itself. `fa-IR` without
//      `-u-ca-persian` silently yields *Gregorian* dates in Persian digits on
//      several runtimes, and even `-u-ca-persian` differs across ICU versions
//      in padding and separators. The calendar math is this file's own
//      `gregorianToJalali`, which the vitest round-trip suite pins.
//
// If `Intl` cannot resolve Asia/Tehran (ancient/stripped runtime) we fall back
// to a fixed +03:30 offset — correct for every instant since Iran abolished DST
// in September 2022, and off by at most one hour for older ones.
// =====================================================

/** Iran standard time, UTC+03:30. DST was abolished in September 2022. */
const TEHRAN_FALLBACK_OFFSET_MIN = 3 * 60 + 30;

interface TehranParts {
  gy: number;
  gm: number;
  gd: number;
  hh: number;
  mi: number;
  ss: number;
}

let tehranFormatter: Intl.DateTimeFormat | null | undefined;

/** Cached `Intl` formatter for Tehran wall-clock parts, or null if unsupported. */
const getTehranFormatter = (): Intl.DateTimeFormat | null => {
  if (tehranFormatter !== undefined) return tehranFormatter;
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    // Prove it actually honours the zone before trusting it.
    const probe = fmt.formatToParts(new Date(Date.UTC(2026, 0, 1, 0, 0)));
    const hour = probe.find((p) => p.type === "hour")?.value;
    tehranFormatter = hour === "03" ? fmt : null;
  } catch {
    tehranFormatter = null;
  }
  return tehranFormatter;
};

/** Gregorian wall-clock parts of an instant, as seen in Tehran. */
const tehranParts = (date: Date): TehranParts => {
  const fmt = getTehranFormatter();
  if (fmt) {
    const parts = fmt.formatToParts(date);
    const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const gy = get("year");
    if (!isNaN(gy)) {
      // Some runtimes render midnight as hour "24"; normalise it to 0.
      const rawHour = get("hour");
      return {
        gy,
        gm: get("month"),
        gd: get("day"),
        hh: rawHour === 24 ? 0 : rawHour,
        mi: get("minute"),
        ss: get("second"),
      };
    }
  }
  const shifted = new Date(date.getTime() + TEHRAN_FALLBACK_OFFSET_MIN * 60_000);
  return {
    gy: shifted.getUTCFullYear(),
    gm: shifted.getUTCMonth() + 1,
    gd: shifted.getUTCDate(),
    hh: shifted.getUTCHours(),
    mi: shifted.getUTCMinutes(),
    ss: shifted.getUTCSeconds(),
  };
};

/** Anything the API hands us → a valid Date, or null. */
const toDate = (d: Date | string | number | null | undefined): Date | null => {
  if (d === null || d === undefined || d === "") return null;
  const date = d instanceof Date ? d : new Date(d);
  return isNaN(date.getTime()) ? null : date;
};

/** Jalali parts of an instant as it falls on the Tehran calendar. */
export const toTehranJalali = (d: Date | string | number | null | undefined): JalaliParts | null => {
  const date = toDate(d);
  if (!date) return null;
  const { gy, gm, gd } = tehranParts(date);
  return gregorianToJalali(gy, gm, gd);
};

/**
 * Gregorian calendar day of an instant *as it falls in Tehran* — the `ar` side
 * of the same anchor rule. The zone stays Asia/Tehran for both languages on
 * purpose: a deadline is one calendar day for the whole university, and moving
 * the anchor per language would make two students disagree about when a term
 * ends. Which calendar to *print* is the localisation decision; which instant
 * a day starts at is not.
 */
export const toTehranGregorian = (
  d: Date | string | number | null | undefined,
): { gy: number; gm: number; gd: number } | null => {
  const date = toDate(d);
  if (!date) return null;
  const { gy, gm, gd } = tehranParts(date);
  return { gy, gm, gd };
};

/** Jalali date + Tehran wall clock of an instant. The inverse of `jalaliDateTimeToIso`. */
export const toTehranJalaliDateTime = (
  d: Date | string | number | null | undefined,
): (JalaliParts & { hour: number; minute: number }) | null => {
  const date = toDate(d);
  if (!date) return null;
  const { gy, gm, gd, hh, mi } = tehranParts(date);
  return { ...gregorianToJalali(gy, gm, gd), hour: hh, minute: mi };
};

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Registry-backed calendar date. The historical fa/ar output remains exact;
 * planned locales use their own Gregorian order, punctuation and digits.
 */
export const formatCalendarDateForLocale = (
  d: Date | string | number | null | undefined,
  locale: AppLocale,
): string => {
  if (localeMetadata(locale)?.calendar === "persian") {
    const j = toTehranJalali(d);
    return j ? toLocaleDigits(`${j.jy}/${pad2(j.jm)}/${pad2(j.jd)}`, locale) : "";
  }
  if (locale === "ar") {
    const g = toTehranGregorian(d);
    return g ? toArDigits(`${g.gy}/${pad2(g.gm)}/${pad2(g.gd)}`) : "";
  }
  const date = toDate(d);
  return date ? formatLocaleDate(date, locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Asia/Tehran",
  }) : "";
};

/** THE canonical active-locale date formatter — «۱۴۰۵/۰۵/۲۵» in fa. */
export const formatJalali = (d: Date | string | number | null | undefined): string =>
  formatCalendarDateForLocale(d, getLocale());

/** Registry-backed long human date, preserving the locked fa/ar wording. */
export const formatCalendarLongDateForLocale = (
  d: Date | string | number | null | undefined,
  locale: AppLocale,
): string => {
  if (localeMetadata(locale)?.calendar === "persian") {
    const j = toTehranJalali(d);
    return j ? `${toLocaleDigits(j.jd, locale)} ${JALALI_MONTHS_FA[j.jm - 1]} ${toLocaleDigits(j.jy, locale)}` : "";
  }
  if (locale === "ar") {
    const g = toTehranGregorian(d);
    return g ? `${toArDigits(g.gd)} ${GREGORIAN_MONTHS_AR[g.gm - 1]} ${toArDigits(g.gy)}` : "";
  }
  const date = toDate(d);
  return date ? formatLocaleDate(date, locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Asia/Tehran",
  }) : "";
};

/** Long active-locale date — «۲۵ مرداد ۱۴۰۵» in fa. */
export const formatJalaliLong = (d: Date | string | number | null | undefined): string =>
  formatCalendarLongDateForLocale(d, getLocale());

/** Tehran wall clock only — «۱۴:۳۰». Calendar-neutral. */
export const formatJalaliTime = (
  d: Date | string | number | null | undefined,
  opts?: { seconds?: boolean },
): string => {
  const date = toDate(d);
  if (!date) return "";
  const { hh, mi, ss } = tehranParts(date);
  const base = `${pad2(hh)}:${pad2(mi)}`;
  return toFaDigits(opts?.seconds ? `${base}:${pad2(ss)}` : base);
};

/** THE canonical date+time formatter — «۱۴۰۵/۰۵/۲۵ — ۱۴:۳۰». */
export const formatJalaliDateTime = (d: Date | string | number | null | undefined): string => {
  const date = toDate(d);
  if (!date) return "";
  return `${formatJalali(date)} — ${formatJalaliTime(date)}`;
};

/** Long date + time — «۲۵ مرداد ۱۴۰۵ — ۱۴:۳۰». */
export const formatJalaliLongDateTime = (d: Date | string | number | null | undefined): string => {
  const date = toDate(d);
  if (!date) return "";
  return `${formatJalaliLong(date)} — ${formatJalaliTime(date)}`;
};

/** Today's Jalali parts, on the Tehran calendar. */
export const jalaliToday = (): JalaliParts => toTehranJalali(new Date()) as JalaliParts;

/**
 * «همین حالا» — امروزِ شمسی + ساعتِ جاریِ تهران. مقدارِ اولیهٔ هر فیلدِ خالیِ
 * تاریخ+ساعت و ساعت از این می‌آید، تا کاربر از نقطهٔ «الان» شروع کند نه از یک
 * ساعتِ قراردادی.
 */
export const jalaliNow = (): JalaliParts & { hour: number; minute: number } =>
  toTehranJalaliDateTime(new Date()) as JalaliParts & { hour: number; minute: number };

/**
 * Jalali day + Tehran wall clock → the ISO instant to store.
 * The inverse of `formatJalaliDateTime`, used by the date+time wheel picker.
 */
export const jalaliDateTimeToIso = (jy: number, jm: number, jd: number, hh: number, mi: number): string => {
  const [gy, gm, gd] = jalaliToGregorian(jy, jm, jd);
  // Start from the naive UTC instant, then correct by the zone's real offset at
  // that moment (so historical pre-2022 DST dates land on the right hour too).
  const naive = Date.UTC(gy, gm - 1, gd, hh, mi);
  const probe = tehranParts(new Date(naive));
  const probeNaive = Date.UTC(probe.gy, probe.gm - 1, probe.gd, probe.hh, probe.mi);
  return new Date(naive - (probeNaive - naive)).toISOString();
};

// =====================================================
// S-I18N-W1a (ADR-A4) — the calendar adapter.
//
// The date pickers are built around `JalaliParts {jy, jm, jd}`. Readers do not
// need separate pickers; they need the SAME wheel filled from registry calendar
// metadata. The parts stay the unit of exchange and only their *meaning* moves:
// Persian calendar for `fa`, Gregorian for the other registered locales. Not
// one picker prop, callback or ISO value changes shape.
//
// The instant a day starts at is NOT part of this decision: all locales stay
// anchored to Tehran (see `toTehranGregorian`), because a deadline has to be
// the same calendar day for the whole university.
// =====================================================

/** Whether a Gregorian year is a leap year (proleptic Gregorian rules). */
const gregorianIsLeap = (gy: number): boolean => (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;

const GREGORIAN_MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Days in a Gregorian month, 1-indexed. */
export const gregorianMonthLength = (gy: number, gm: number): number =>
  gm === 2 && gregorianIsLeap(gy) ? 29 : GREGORIAN_MONTH_LENGTHS[gm - 1];

/**
 * The calendar the active locale reads in.
 *
 * Every member speaks in `JalaliParts` so the pickers can swap adapters
 * without touching their state, their props or the ISO strings they emit.
 */
export interface CalendarAdapter {
  /** The locale this adapter serves — handy for keying memoised lists. */
  readonly locale: AppLocale;
  /** Whether the parts are Jalali or Gregorian according to locale metadata. */
  readonly system: "jalali" | "gregorian";
  /** Month names, index 0 = month 1, already in the reader's language. */
  readonly months: readonly string[];
  /** Weekday initials, index 0 = the first column of the month grid. */
  readonly weekdays: readonly string[];
  /** Weekday of the grid's first column, as a JS `Date.getDay()` value. */
  readonly firstWeekday: number;
  /** Days in a month of this calendar. */
  monthLength(y: number, m: number): number;
  /** Weekday as JavaScript day number (0=Sunday … 6=Saturday). */
  weekday(y: number, m: number, d: number): number;
  /** Today, on the Tehran calendar day. */
  today(): JalaliParts;
  /** The parts of an instant, or null when it is not a date. */
  fromInstant(d: Date | string | number | null | undefined): JalaliParts | null;
  /** UTC-midnight ISO of a picked day — what the API stores. */
  toIso(y: number, m: number, d: number): string;
  /** Picked day + Tehran wall clock → the ISO instant to store. */
  toIsoDateTime(y: number, m: number, d: number, hh: number, mi: number): string;
  /** Today + the Tehran wall clock - what an empty date+time field starts at. */
  nowParts(): JalaliParts & { hour: number; minute: number };
  /** Parts + Tehran wall clock of an instant, or null when it is not a date. */
  fromInstantDateTime(
    d: Date | string | number | null | undefined,
  ): (JalaliParts & { hour: number; minute: number }) | null;
}

const JALALI_ADAPTER: CalendarAdapter = {
  locale: "fa",
  system: "jalali",
  months: JALALI_MONTHS_FA,
  weekdays: JALALI_WEEKDAYS_FA,
  firstWeekday: 6, // Saturday — the Iranian week starts on شنبه
  monthLength: jalaliMonthLength,
  weekday: (jy, jm, jd) => jalaliToDate(jy, jm, jd).getDay(),
  today: jalaliToday,
  fromInstant: toTehranJalali,
  toIso: jalaliToIso,
  toIsoDateTime: jalaliDateTimeToIso,
  nowParts: jalaliNow,
  fromInstantDateTime: toTehranJalaliDateTime,
};

interface WeekInfoLike { firstDay: number }
type LocaleWithWeekInfo = Intl.Locale & {
  getWeekInfo?: () => WeekInfoLike;
  weekInfo?: WeekInfoLike;
};

const firstWeekdayFor = (locale: AppLocale): number => {
  // Preserve the established Saturday-first fa/ar contract. Other locales use
  // CLDR week data when the runtime exposes it, with Monday as the safe fallback.
  if (locale === "fa" || locale === "ar") return 6;
  try {
    const resolved = new Intl.Locale(intlLocale(locale)) as LocaleWithWeekInfo;
    const info = typeof resolved.getWeekInfo === "function" ? resolved.getWeekInfo() : resolved.weekInfo;
    if (info && Number.isInteger(info.firstDay) && info.firstDay >= 1 && info.firstDay <= 7) {
      return info.firstDay % 7;
    }
  } catch {
    // The adapter remains deterministic on runtimes without Intl.Locale week data.
  }
  return 1;
};

const gregorianMonthNames = (locale: AppLocale): readonly string[] => {
  if (locale === "ar") return GREGORIAN_MONTHS_AR;
  const formatter = localeDateFormatter(intlLocale(locale), { month: "long", timeZone: "UTC" });
  return Array.from({ length: 12 }, (_, month) => formatter.format(new Date(Date.UTC(2024, month, 1))));
};

const gregorianWeekdayNames = (locale: AppLocale, firstWeekday: number): readonly string[] => {
  if (locale === "ar") return GREGORIAN_WEEKDAYS_AR;
  const formatter = localeDateFormatter(intlLocale(locale), { weekday: "short", timeZone: "UTC" });
  const sunday = Date.UTC(2024, 0, 7);
  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(new Date(sunday + ((firstWeekday + index) % 7) * 86_400_000)));
};

const createGregorianAdapter = (locale: AppLocale): CalendarAdapter => {
  const firstWeekday = firstWeekdayFor(locale);
  return {
    locale,
    system: "gregorian",
    months: gregorianMonthNames(locale),
    weekdays: gregorianWeekdayNames(locale, firstWeekday),
    firstWeekday,
    monthLength: gregorianMonthLength,
    weekday: (gy, gm, gd) => new Date(Date.UTC(gy, gm - 1, gd)).getUTCDay(),
    today: () => {
      const g = toTehranGregorian(new Date()) as { gy: number; gm: number; gd: number };
      return { jy: g.gy, jm: g.gm, jd: g.gd };
    },
    fromInstant: (d) => {
      const g = toTehranGregorian(d);
      return g ? { jy: g.gy, jm: g.gm, jd: g.gd } : null;
    },
    toIso: (gy, gm, gd) => new Date(Date.UTC(gy, gm - 1, gd)).toISOString(),
    toIsoDateTime: (gy, gm, gd, hh, mi) => {
      // Same correction dance as `jalaliDateTimeToIso`: build the naive instant,
      // then subtract the zone offset Tehran actually had at that moment.
      const naive = Date.UTC(gy, gm - 1, gd, hh, mi);
      const probe = tehranParts(new Date(naive));
      const probeNaive = Date.UTC(probe.gy, probe.gm - 1, probe.gd, probe.hh, probe.mi);
      return new Date(naive - (probeNaive - naive)).toISOString();
    },
    nowParts: () => {
      const t = tehranParts(new Date());
      return { jy: t.gy, jm: t.gm, jd: t.gd, hour: t.hh, minute: t.mi };
    },
    fromInstantDateTime: (d) => {
      const date = toDate(d);
      if (!date) return null;
      const t = tehranParts(date);
      return { jy: t.gy, jm: t.gm, jd: t.gd, hour: t.hh, minute: t.mi };
    },
  };
};

const gregorianAdapters = new Map<AppLocale, CalendarAdapter>();

const gregorianAdapterFor = (locale: AppLocale): CalendarAdapter => {
  const cached = gregorianAdapters.get(locale);
  if (cached) return cached;
  const adapter = createGregorianAdapter(locale);
  gregorianAdapters.set(locale, adapter);
  return adapter;
};

/** The calendar for one locale (pure — safe to call in a `useMemo`). */
export const calendarFor = (locale: AppLocale): CalendarAdapter =>
  localeMetadata(locale)?.calendar === "persian"
    ? JALALI_ADAPTER
    : gregorianAdapterFor(locale);

/** Registry-calendar month + year label for charts, filters and headings. */
export const formatCalendarMonthForLocale = (
  value: Date | string | number | null | undefined,
  locale: AppLocale,
): string => {
  const cal = calendarFor(locale);
  const parts = cal.fromInstant(value);
  return parts ? `${cal.months[parts.jm - 1]} ${toLocaleDigits(parts.jy, locale)}` : "";
};

/** Calendar year/month identity for locale-aware grouping and KPI buckets. */
export const calendarYearMonthForLocale = (
  value: Date | string | number | null | undefined,
  locale: AppLocale,
): Pick<JalaliParts, "jy" | "jm"> | null => {
  const parts = calendarFor(locale).fromInstant(value);
  return parts ? { jy: parts.jy, jm: parts.jm } : null;
};

/**
 * Gregorian ISO-day bounds for the reader locale's calendar month containing
 * `base`. API range contracts stay calendar-neutral while the visible month
 * follows registry metadata (Persian for fa, Gregorian for the other locales).
 */
export const calendarMonthBoundsForLocale = (
  base: Date | string | number,
  locale: AppLocale,
): { from: string; to: string } => {
  const cal = calendarFor(locale);
  const parts = cal.fromInstant(base) ?? cal.today();
  const lastDay = cal.monthLength(parts.jy, parts.jm);
  return {
    from: cal.toIso(parts.jy, parts.jm, 1).slice(0, 10),
    to: cal.toIso(parts.jy, parts.jm, lastDay).slice(0, 10),
  };
};

/**
 * The schedule API stores weekdays as 0=Saturday … 6=Friday. Keep that wire
 * contract stable while calendar surfaces reorder columns for each locale's
 * registry/CLDR first weekday.
 */
export const scheduleDayIndexFromJsWeekday = (weekday: number): number =>
  (((weekday % 7) + 7) % 7 + 1) % 7;

/** Schedule weekday ids, ordered for the reader locale's visible week. */
export const scheduleDayIndexesForLocale = (locale: AppLocale): readonly number[] => {
  const firstWeekday = calendarFor(locale).firstWeekday;
  return Array.from({ length: 7 }, (_, offset) =>
    scheduleDayIndexFromJsWeekday((firstWeekday + offset) % 7));
};

/** The calendar the reader is currently on. */
export const activeCalendar = (): CalendarAdapter => calendarFor(getLocale());
